import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Package, Truck, AlertTriangle, Check, X, DollarSign,
  MapPin, User, Clock, RefreshCw, Loader2, Search,
  CreditCard, Receipt, ExternalLink, ChevronRight, Edit, Ban
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  processing: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  confirmed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  shipped: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
  delivered: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  issue: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  cancelled: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  refunded: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300'
};

/**
 * AdminOrderManagement - Manage marketplace orders, fix issues, process refunds
 */
export default function AdminOrderManagement() {
  const { getAuthHeaders } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showRefundDialog, setShowRefundDialog] = useState(false);
  const [search, setSearch] = useState('');
  const [summary, setSummary] = useState({ total: 0, pending: 0, processing: 0, issues: 0 });
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    status: '',
    admin_notes: '',
    adjusted_price: '',
    adjusted_shipping: ''
  });
  
  // Refund form state
  const [refundForm, setRefundForm] = useState({
    amount: '',
    reason: ''
  });
  
  useEffect(() => {
    fetchOrders();
  }, [activeTab]);
  
  const fetchOrders = async () => {
    setLoading(true);
    try {
      const status = activeTab === 'all' ? '' : activeTab;
      const response = await axios.get(`${API}/orders/admin/all?status=${status}`, {
        headers: getAuthHeaders()
      });
      setOrders(response.data.orders || []);
      setSummary(response.data.summary || {});
    } catch (err) {
      console.error('Failed to fetch orders:', err);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };
  
  const handleConfirmOrder = async (orderId) => {
    try {
      await axios.post(`${API}/orders/admin/${orderId}/confirm`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('Order confirmed and customer charged');
      fetchOrders();
    } catch (err) {
      toast.error('Failed to confirm order');
    }
  };
  
  const handleUpdateOrder = async () => {
    if (!selectedOrder) return;
    
    try {
      const updateData = {};
      if (editForm.status) updateData.status = editForm.status;
      if (editForm.admin_notes) updateData.admin_notes = editForm.admin_notes;
      if (editForm.adjusted_price) updateData.adjusted_price = parseFloat(editForm.adjusted_price);
      if (editForm.adjusted_shipping) updateData.adjusted_shipping = parseFloat(editForm.adjusted_shipping);
      
      await axios.put(`${API}/orders/admin/${selectedOrder.order_id}`, updateData, {
        headers: getAuthHeaders()
      });
      
      toast.success('Order updated');
      setShowEditDialog(false);
      setSelectedOrder(null);
      fetchOrders();
    } catch (err) {
      toast.error('Failed to update order');
    }
  };
  
  const handleRefund = async () => {
    if (!selectedOrder || !refundForm.amount) return;
    
    try {
      await axios.post(
        `${API}/orders/admin/${selectedOrder.order_id}/refund?amount=${refundForm.amount}&reason=${encodeURIComponent(refundForm.reason)}`,
        {},
        { headers: getAuthHeaders() }
      );
      
      toast.success(`Refunded $${refundForm.amount}`);
      setShowRefundDialog(false);
      setSelectedOrder(null);
      setRefundForm({ amount: '', reason: '' });
      fetchOrders();
    } catch (err) {
      toast.error('Failed to process refund');
    }
  };
  
  const handleFlagIssue = async (orderId, issue) => {
    try {
      await axios.post(
        `${API}/orders/admin/${orderId}/flag-issue?issue=${encodeURIComponent(issue)}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('Issue flagged');
      fetchOrders();
    } catch (err) {
      toast.error('Failed to flag issue');
    }
  };
  
  const openEditDialog = (order) => {
    setSelectedOrder(order);
    setEditForm({
      status: order.status,
      admin_notes: order.admin_notes || '',
      adjusted_price: '',
      adjusted_shipping: ''
    });
    setShowEditDialog(true);
  };
  
  const openRefundDialog = (order) => {
    setSelectedOrder(order);
    setRefundForm({
      amount: order.total.toFixed(2),
      reason: ''
    });
    setShowRefundDialog(true);
  };
  
  const filteredOrders = orders.filter(order => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      order.order_id.toLowerCase().includes(searchLower) ||
      order.customer_name?.toLowerCase().includes(searchLower) ||
      order.customer_email?.toLowerCase().includes(searchLower)
    );
  });
  
  return (
    <div className="space-y-6" data-testid="admin-order-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            Order Management
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Process orders, fix issues, manage refunds
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchOrders}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-lg sm:text-xl font-bold">{summary.total}</p>
                <p className="text-xs text-gray-500">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-yellow-600" />
              <div>
                <p className="text-lg sm:text-xl font-bold text-yellow-600">{summary.pending}</p>
                <p className="text-xs text-gray-500">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2">
              <Loader2 className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-lg sm:text-xl font-bold text-blue-600">{summary.processing}</p>
                <p className="text-xs text-gray-500">Processing</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <div>
                <p className="text-lg sm:text-xl font-bold text-red-600">{summary.issues}</p>
                <p className="text-xs text-gray-500">Issues</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by order ID, customer name, or email..."
          className="pl-10"
        />
      </div>
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full flex overflow-x-auto">
          <TabsTrigger value="all" className="flex-1">All</TabsTrigger>
          <TabsTrigger value="pending" className="flex-1">Pending</TabsTrigger>
          <TabsTrigger value="issue" className="flex-1">
            Issues
            {summary.issues > 0 && (
              <Badge className="ml-1 bg-red-500">{summary.issues}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="confirmed" className="flex-1">Confirmed</TabsTrigger>
          <TabsTrigger value="refunded" className="flex-1">Refunded</TabsTrigger>
        </TabsList>
        
        <TabsContent value={activeTab} className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : filteredOrders.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Package className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No orders found</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredOrders.map((order) => (
                <OrderCard
                  key={order.order_id}
                  order={order}
                  onConfirm={() => handleConfirmOrder(order.order_id)}
                  onEdit={() => openEditDialog(order)}
                  onRefund={() => openRefundDialog(order)}
                  onFlagIssue={(issue) => handleFlagIssue(order.order_id, issue)}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
      
      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Order</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Status</label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="shipped">Shipped</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                  <SelectItem value="issue">Issue</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1 block">Adjust Total Price</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="number"
                  value={editForm.adjusted_price}
                  onChange={(e) => setEditForm({ ...editForm, adjusted_price: e.target.value })}
                  placeholder={selectedOrder?.total?.toFixed(2)}
                  className="pl-8"
                  step="0.01"
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1 block">Adjust Shipping</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="number"
                  value={editForm.adjusted_shipping}
                  onChange={(e) => setEditForm({ ...editForm, adjusted_shipping: e.target.value })}
                  placeholder={selectedOrder?.shipping_total?.toFixed(2)}
                  className="pl-8"
                  step="0.01"
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1 block">Admin Notes</label>
              <Textarea
                value={editForm.admin_notes}
                onChange={(e) => setEditForm({ ...editForm, admin_notes: e.target.value })}
                placeholder="Internal notes about this order..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateOrder}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Refund Dialog */}
      <Dialog open={showRefundDialog} onOpenChange={setShowRefundDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Process Refund</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Order Total: <strong>${selectedOrder?.total?.toFixed(2)}</strong>
            </p>
            
            <div>
              <label className="text-sm font-medium mb-1 block">Refund Amount</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="number"
                  value={refundForm.amount}
                  onChange={(e) => setRefundForm({ ...refundForm, amount: e.target.value })}
                  max={selectedOrder?.total}
                  className="pl-8"
                  step="0.01"
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1 block">Reason</label>
              <Textarea
                value={refundForm.reason}
                onChange={(e) => setRefundForm({ ...refundForm, reason: e.target.value })}
                placeholder="Reason for refund..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRefundDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleRefund}>
              Process Refund
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * OrderCard - Individual order card
 */
function OrderCard({ order, onConfirm, onEdit, onRefund, onFlagIssue }) {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <Card className={order.status === 'issue' ? 'border-red-300 dark:border-red-700' : ''}>
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <div>
            <p className="font-mono text-sm text-gray-500">#{order.order_id.slice(0, 8)}</p>
            <p className="font-semibold">{order.customer_name}</p>
            <p className="text-xs text-gray-500">{order.customer_email}</p>
          </div>
          <Badge className={STATUS_COLORS[order.status] || STATUS_COLORS.pending}>
            {order.status}
          </Badge>
        </div>
        
        {/* Items preview */}
        <div className="flex items-center gap-2 mb-3">
          {order.items?.slice(0, 3).map((item, idx) => (
            <div key={idx} className="w-10 h-10 bg-gray-100 dark:bg-gray-800 rounded overflow-hidden">
              {item.image ? (
                <img src={item.image} alt="" className="w-full h-full object-cover" />
              ) : (
                <Package className="w-full h-full p-2 text-gray-400" />
              )}
            </div>
          ))}
          {order.items?.length > 3 && (
            <span className="text-xs text-gray-500">+{order.items.length - 3} more</span>
          )}
        </div>
        
        {/* Totals */}
        <div className="flex items-center justify-between text-sm mb-3">
          <span className="text-gray-500">{order.items?.length || 0} item(s)</span>
          <span className="font-bold text-lg">${order.total?.toFixed(2)}</span>
        </div>
        
        {/* Issues */}
        {order.issues?.length > 0 && (
          <div className="bg-red-50 dark:bg-red-900/20 p-2 rounded mb-3">
            <p className="text-xs text-red-600 dark:text-red-400 font-medium">
              <AlertTriangle className="w-3 h-3 inline mr-1" />
              {order.issues[order.issues.length - 1].description}
            </p>
          </div>
        )}
        
        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          {order.status === 'pending' && (
            <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={onConfirm}>
              <Check className="w-3 h-3 mr-1" />
              Confirm & Charge
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={onEdit}>
            <Edit className="w-3 h-3 mr-1" />
            Edit
          </Button>
          {order.status !== 'refunded' && order.status !== 'cancelled' && (
            <Button size="sm" variant="outline" className="text-red-600" onClick={onRefund}>
              <CreditCard className="w-3 h-3 mr-1" />
              Refund
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setExpanded(!expanded)}
          >
            Details
            <ChevronRight className={`w-3 h-3 ml-1 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          </Button>
        </div>
        
        {/* Expanded Details */}
        {expanded && (
          <div className="mt-4 pt-4 border-t dark:border-gray-700 space-y-3">
            {/* Shipping Address */}
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">Shipping Address</p>
              <p className="text-sm">
                {order.shipping_address?.name}<br />
                {order.shipping_address?.street}<br />
                {order.shipping_address?.city}, {order.shipping_address?.state} {order.shipping_address?.zip_code}
              </p>
            </div>
            
            {/* Items */}
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">Items</p>
              <div className="space-y-2">
                {order.items?.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm">
                    <span>{item.title} × {item.quantity}</span>
                    <span>${item.item_total?.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Breakdown */}
            <div className="text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-gray-500">Subtotal</span>
                <span>${order.subtotal?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Shipping</span>
                <span>${order.shipping_total?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Tax</span>
                <span>${order.tax?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-bold">
                <span>Total</span>
                <span>${order.total?.toFixed(2)}</span>
              </div>
            </div>
            
            {/* Admin Notes */}
            {order.admin_notes && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 p-2 rounded">
                <p className="text-xs text-yellow-800 dark:text-yellow-300">
                  <strong>Admin Note:</strong> {order.admin_notes}
                </p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
